import { useCallback } from 'react';
import { useTheme } from 'styled-components';

import { Icon } from '@/core/components/Icons';

import {
  ReportCategory,
  ReportCountry,
  ReportResponseItem,
  ReportSource,
} from '@/features/Reports/interfaces';

import { DownloadCSVFilesProps } from './DownloadCSVFile.types';
import { DownloadButton } from './DownloadCSVFile.styles';

const getReportCategories = (categories: Array<ReportCategory>): string =>
  categories
    .map(category =>
      category.mainCategory.subcategories
        .map(subcategory => subcategory.name.replaceAll('&amp;', '&'))
        .join(' + ')
    )
    .join(' ')
    .trim();

const getReportCountries = (countries: Array<ReportCountry>): string =>
  countries
    .map(country => country.region.countries.map(region => region.name).join(' - '))
    .join(' ')
    .trim();

const getReportSources = (sources: Array<ReportSource>): string =>
  sources.map(source => source.url).join(' ');

const prepareContent = (content?: string): string =>
  content ? content.replaceAll('"', "'").replaceAll('\n', '') : '-';

export const generateReportsCSV = (reports: Array<ReportResponseItem>) => {
  const csvContentData: Array<Array<string | number>> = [
    [
      '"UUID"',
      '"Date"',
      '"Categories"',
      '"Impact"',
      '"Countries"',
      '"Latitude"',
      '"Longitude"',
      '"Killed"',
      '"Wounded"',
      '"Hostages"',
      '"Missing"',
      '"Arrested"',
      '"Type"',
      '"Title"',
      '"Content"',
      '"Source"',
      '"Published on"',
      '"Edited on"',
    ],
  ];

  reports.forEach(report => {
    csvContentData.push([
      `"${report.reportId}"`,
      `"${report.eventDate}"`,
      `"${getReportCategories(report.category)}"`,
      `"${report.impact}"`,
      `"${getReportCountries(report.country)}"`,
      `"${report.location[0].latitude}"`,
      `"${report.location[0].longitude}"`,
      `"${report.victims.dead}"`,
      `"${report.victims.wounded}"`,
      `"${report.victims.hostages}"`,
      `"${report.victims.missing}"`,
      `"${report.victims.arrested}"`,
      `"${report.reportType}"`,
      `"${prepareContent(report.reportTitle)}"`,
      `"${prepareContent(report.content)}"`,
      `"${getReportSources(report.source)}"`,
      `"${report.publishedDatetime}"`,
      `"${report.modifiedDatetime}"`,
    ]);
  });

  const csvContent = csvContentData.map(row => row.join(',')).join('\n');

  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });

  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');

  link.setAttribute('href', url);
  link.setAttribute('download', 'reports.csv');
  document.body.appendChild(link);
  link.click();

  document.body.removeChild(link);
};

export const DownloadCSVFile = ({ reports }: DownloadCSVFilesProps) => {
  const { colors } = useTheme();

  const onDownloadClick = useCallback(() => {
    generateReportsCSV(reports);
  }, [reports]);

  return (
    <DownloadButton
      type="button"
      onClick={onDownloadClick}
    >
      <Icon
        name="Upload"
        size={16}
        color={colors.secondaryText}
      />
    </DownloadButton>
  );
};
